import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { StatusBadge, formatDate, useIsAdmin } from '../components/shared';
import NewTicketModal from '../components/NewTicketModal';

const STATUSES = ['received', 'in_repair', 'done', 'delivered'];
const STATUS_LABELS = { received: 'Received', in_repair: 'In Repair', done: 'Done', delivered: 'Delivered' };
const STATUS_COLORS = { received: 'var(--primary)', in_repair: 'var(--yellow)', done: 'var(--green)', delivered: 'var(--text3)' };

function toggleSet(set, val) {
  const s = new Set(set);
  s.has(val) ? s.delete(val) : s.add(val);
  return s;
}

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [statusFilters, setStatusFilters] = useState(new Set());
  const [clientFilters, setClientFilters] = useState(new Set());
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(() => {
    api.getTickets({}).then(setTickets).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.getClients().then(setClients).catch(() => {});
  }, [load]);

  const filtered = tickets.filter(t => {
    if (statusFilters.size > 0 && !statusFilters.has(t.status)) return false;
    if (clientFilters.size > 0 && !clientFilters.has(t.client_id || '')) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        t.device_name?.toLowerCase().includes(s) ||
        t.device_serial?.toLowerCase().includes(s) ||
        t.device_alias?.toLowerCase().includes(s) ||
        t.fault_description?.toLowerCase().includes(s) ||
        t.client_name?.toLowerCase().includes(s)
      );
    }
    return true;
  });

  const activeFilters = statusFilters.size + clientFilters.size;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">TICKETS <span>_</span></h1>
        <div className="flex gap-8">
        {isAdmin && <button className="btn btn-primary" onClick={() => setNewOpen(true)}>+ New Ticket</button>}
        {activeFilters > 0 && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setStatusFilters(new Set()); setClientFilters(new Set()); }}>
            ✕ Clear filters ({activeFilters})
          </button>
        )}
        </div>
      </div>
      {newOpen && <NewTicketModal onClose={() => setNewOpen(false)} />}

      {/* Filter bar */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Status</div>
        <div className="flex gap-8" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
          {STATUSES.map(s => {
            const active = statusFilters.has(s);
            return (
              <button key={s}
                onClick={() => setStatusFilters(prev => toggleSet(prev, s))}
                style={{
                  padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', border: `2px solid ${active ? STATUS_COLORS[s] : 'var(--border)'}`,
                  background: active ? STATUS_COLORS[s] + '22' : 'transparent',
                  color: active ? STATUS_COLORS[s] : 'var(--text3)',
                  transition: 'all 0.15s',
                }}>
                {active ? '✓ ' : ''}{STATUS_LABELS[s]}
              </button>
            );
          })}
        </div>

        {clients.length > 0 && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>Client</div>
            <div className="flex gap-8" style={{ flexWrap: 'wrap', marginBottom: 12 }}>
              {clients.map(c => {
                const active = clientFilters.has(c.id);
                return (
                  <button key={c.id}
                    onClick={() => setClientFilters(prev => toggleSet(prev, c.id))}
                    style={{
                      padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      cursor: 'pointer', border: `2px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                      background: active ? 'var(--primary-dim)' : 'transparent',
                      color: active ? 'var(--primary)' : 'var(--text3)',
                      transition: 'all 0.15s',
                    }}>
                    {active ? '✓ ' : ''}{c.name}
                  </button>
                );
              })}
            </div>
          </>
        )}

        <div className="search-wrap" style={{ maxWidth: 360 }}>
          <input className="input" placeholder="Search device, serial, fault, client..."
            value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Client</th>
              <th>Device</th>
              <th>Serial</th>
              <th>Fault</th>
              <th>Status</th>
              <th>Parts</th>
              <th>Invoice</th>
              <th>Received</th>
              <th>Return</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={10}><div className="empty">No tickets found</div></td></tr>
            ) : filtered.map(t => (
              <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tickets/${t.id}`)}>
                <td><span className="mono text-sm text-dim">{t.id.slice(0, 8)}</span></td>
                <td className="text-sm">{t.client_name ? <span className="badge badge-blue">{t.client_name}</span> : <span className="text-dim">—</span>}</td>
                <td className="fw-500">
                  {t.device_name}
                  {t.device_alias && <div className="text-dim text-xs mono">{t.device_alias}</div>}
                </td>
                <td><span className="mono text-sm">{t.device_serial}</span></td>
                <td className="text-sm text-muted">{t.fault_description || '—'}</td>
                <td><StatusBadge status={t.status} /></td>
                <td><span className="mono text-sm">{t.parts_count}</span></td>
                <td><span className="mono text-sm">{t.invoice_count}</span></td>
                <td className="mono text-sm text-dim">{formatDate(t.received_at)}</td>
                <td>{t.parent_ticket_id ? <span className="badge badge-red text-xs">↩ Return</span> : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
